// Reprise des recettes d'un fichier Excel (onglet type « INTRANT » : une ligne par poteau, une colonne « Fer 6 (kg / poteau) »…)
// et des prix (onglet type « BUDGET INTRANTS » : DÉSIGNATION, UNITÉ, PU), avec correspondance vers les articles du stock et conversion d'unités.
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { UploadFileOutlined } from '@mui/icons-material';
import { lireClasseur, norme } from '../../utils/excel';
import { apiError } from '../../utils/format';
import { planningAPI } from '../../services/api';

const cleType = (v) => norme(v).replace(/option\s*\d+/g, '').replace(/[\s-]/g, '').toUpperCase();
const ressembleType = (v) => /^\d{1,2}(AR|B)\d{2,4}$/.test(cleType(v));
const SAC_KG = 50;
/** Nom de l'article dans l'ERP : le « basalte » du fichier s'appelle « Béton » (Béton 3/8, 8/16, 0/3, 16/25). */
const nomArticle = (nom) => String(nom).replace(/^basalte/i, 'Béton');
const nf = (n, d = 3) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: d }));
const num = (v) => (typeof v === 'number' ? v : Number(String(v ?? '').replace(/\s/g, '').replace(',', '.')));

/** Unité source (« kg », « m3 », « t », « sac », « l ») → quantité dans l'unité de l'article (unite), selon la densité (t/m³). */
export function facteur(src, unite, densite = 1.6) {
  const u = norme(unite); const s = norme(src).replace('³', '3');
  const enKg = { kg: 1, t: 1000, sac: SAC_KG, m3: densite * 1000 }[s];
  if (s === u || (s === 'm3' && u === 'm3')) return 1;
  if (s === 'l' || u === 'l') return s === u ? 1 : null;
  if (enKg === undefined) return null;
  if (u === 'kg') return enKg;
  if (u === 't') return enKg / 1000;
  if (u === 'sac') return enKg / SAC_KG;
  if (u === 'm3') return s === 'm3' ? 1 : enKg / (densite * 1000);
  return null;
}

/** Repère l'onglet des recettes, ses colonnes d'intrants et ses lignes de poteaux. */
export function lireRecettes(feuilles) {
  for (const f of feuilles) {
    const iH = f.lignes.findIndex((r) => r.filter((c) => /\/\s*poteau/i.test(String(c ?? ''))).length >= 3);
    if (iH < 0) continue;
    const h = f.lignes[iH];
    const colonnes = h.map((c, j) => {
      const m = /^(.*?)\s*\(\s*([a-zA-Z0-9³]+)\s*\/\s*poteau\s*\)/i.exec(String(c ?? ''));
      return m ? { j, nom: m[1].trim(), src: m[2].toLowerCase().replace('³', '3') } : null;
    }).filter(Boolean);
    const lignes = [];
    for (const r of f.lignes.slice(iH + 1)) {
      if (!ressembleType(r[0])) { if (lignes.length && /^total/i.test(String(r[0] ?? ''))) break; continue; }
      const opt = /option\s*(\d+)/i.exec(String(r[0]));
      lignes.push({ libelle: String(r[0]), cle: cleType(r[0]), option: opt ? Number(opt[1]) : null, valeurs: colonnes.map((c) => { const v = num(r[c.j]); return Number.isFinite(v) && v > 0 ? v : 0; }) });
    }
    // Option retenue du 9 AR 150 (onglet des paramètres), si le fichier la précise
    let optionParam = null;
    feuilles.forEach((g) => g.lignes.forEach((r) => {
      const i = r.findIndex((c) => /recette .*option/i.test(String(c ?? '')));
      if (i >= 0) { const v = r.slice(i + 1).find((c) => typeof c === 'number'); if (v) optionParam = v; }
    }));
    // Prix : onglet avec DÉSIGNATION / UNITÉ / PU
    const prix = {};
    feuilles.forEach((g) => {
      const iP = g.lignes.findIndex((r) => r.some((c) => /^designation/.test(norme(c))) && r.some((c) => /^(pu|prix)/.test(norme(c))));
      if (iP < 0) return;
      const hp = g.lignes[iP].map(norme);
      const cD = hp.findIndex((c) => /^designation/.test(c)); const cU = hp.findIndex((c) => /^unite/.test(c)); const cP = hp.findIndex((c) => /^(pu|prix)/.test(c));
      g.lignes.slice(iP + 1).forEach((r) => {
        const p = num(r[cP]);
        if (r[cD] && Number.isFinite(p) && p > 0) prix[norme(r[cD])] = { pu: p, unite: cU >= 0 ? norme(r[cU]).replace('³', '3') : null, feuille: g.feuille };
      });
    });
    return { feuille: f.feuille, colonnes, lignes, optionParam, prix };
  }
  return null;
}

/** Article du stock le plus probable pour une colonne du fichier. */
function deviner(nom, intrants) {
  const n = norme(nom);
  const actifs = intrants;
  const fer = /^fer\s*(\d+)/.exec(n);
  if (fer) {
    const d = Number(fer[1]);
    const local = actifs.find((i) => i.code === `FER${d}L`); const imp = actifs.find((i) => i.code === `FER${d}`);
    return ([6, 8, 10].includes(d) ? local || imp : imp || local)?.intrant_id || null;
  }
  if (/ciment/.test(n)) { const c = actifs.find((i) => /ciment/.test(norme(i.designation)) && n.replace(/\D/g, '').slice(0, 3) === norme(i.designation).replace(/\D/g, '').slice(0, 3)); return (c || actifs.find((i) => i.code === 'CIMENT'))?.intrant_id || null; }
  const exact = actifs.find((i) => norme(i.designation) === n);
  if (exact) return exact.intrant_id;
  const g = /(\d+\/\d+)/.exec(n);
  if (g) { const x = actifs.find((i) => norme(i.designation).includes(g[1])); if (x) return x.intrant_id; }
  if (/^sable/.test(n)) return actifs.find((i) => i.code === 'SABLE')?.intrant_id || null;
  return null;
}

const RecettesDialog = ({ open, d, onClose, onSaved }) => {
  const [fichier, setFichier] = useState(null);
  const [rec, setRec] = useState(null);
  const [map, setMap] = useState({}); // colonne → { cible: id | 'creer' | 'ignorer', densite, unite }
  const [options, setOptions] = useState({}); // type → option
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef();
  const intrants = (d?.intrants || []).filter((i) => i.pour_pba !== false); // pas le ciment 32,5 R (briques, pavés)

  const charger = async (f) => {
    if (!f) return;
    setFichier(f); setRec(null); setError('');
    try {
      const r = lireRecettes(await lireClasseur(f));
      if (!r) throw new Error('Aucun onglet de recettes trouvé : il faut des colonnes du type « Fer 10 (kg / poteau) » et une ligne par type de poteau.');
      setRec(r);
      setMap(Object.fromEntries(r.colonnes.map((c) => {
        const id = deviner(c.nom, intrants);
        return [c.j, { cible: id || 'creer', densite: '1,6', unite: ['kg', 'm3', 'sac'].includes(c.src) ? c.src : 't' }];
      })));
      const opts = {};
      r.lignes.filter((l) => l.option).forEach((l) => { opts[l.cle] = String(r.optionParam || 1); });
      setOptions(opts);
    } catch (e) { setError(e.message); }
  };

  const calc = useMemo(() => {
    if (!rec) return null;
    const types = d.types;
    const parCle = Object.fromEntries(types.map((t) => [cleType(t), t]));
    const cols = rec.colonnes.map((c) => {
      const m = map[c.j] || {}; const it = intrants.find((i) => i.intrant_id === m.cible);
      const unite = m.cible === 'creer' ? m.unite : it?.unite;
      const k = m.cible === 'ignorer' ? null : facteur(c.src, unite, num(m.densite) || 1.6);
      const p = rec.prix[norme(c.nom)];
      const kp = p?.unite ? facteur(p.unite, unite, num(m.densite) || 1.6) : null;
      return { ...c, m, it, unite, k, prixExcel: p, prix: p && kp ? Math.round((p.pu / kp) * 100) / 100 : null };
    });
    const lignes = []; const inconnus = new Set(); const ignorees = [];
    rec.lignes.forEach((l) => {
      const t = parCle[l.cle];
      if (!t) { inconnus.add(l.libelle.replace(/option\s*\d+/i, '').trim()); return; }
      if (l.option && String(l.option) !== options[l.cle]) { ignorees.push(l.libelle); return; }
      lignes.push({ type: t, libelle: l.libelle, valeurs: l.valeurs });
    });
    const erreurs = cols.filter((c) => c.m.cible !== 'ignorer' && c.k === null).map((c) => `${c.nom} : conversion ${c.src} → ${c.unite || '?'} impossible, choisissez un autre article`);
    return { cols, lignes, inconnus: [...inconnus], ignorees, erreurs };
  }, [rec, map, options, d, intrants]);

  const setM = (j, k, v) => setMap((x) => ({ ...x, [j]: { ...x[j], [k]: v } }));
  const importer = async () => {
    setBusy(true); setError('');
    try {
      const creer = calc.cols.filter((c) => c.m.cible === 'creer').map((c) => ({ cle: `c${c.j}`, designation: nomArticle(c.nom), unite: c.unite, categorie: /^fer/i.test(c.nom) ? 'FER_IMPORTE' : /ciment/i.test(c.nom) ? 'CIMENT' : 'GRANULAT' }));
      const ref = (c) => (c.m.cible === 'creer' ? { cle: `c${c.j}` } : { intrant_id: c.m.cible });
      const valeurs = calc.lignes.flatMap((l) => calc.cols.map((c, i) => (c.m.cible === 'ignorer' || !l.valeurs[i] ? null
        : { pba_type: l.type, ...ref(c), quantite: Math.round(l.valeurs[i] * c.k * 10000) / 10000 })).filter(Boolean));
      // Un même article peut recevoir plusieurs colonnes (somme) : un seul prix par article
      const vus = new Set();
      const prix = calc.cols.filter((c) => c.m.cible !== 'ignorer' && c.prix !== null).filter((c) => { const k = JSON.stringify(ref(c)); if (vus.has(k)) return false; vus.add(k); return true; })
        .map((c) => ({ ...ref(c), prix: c.prix }));
      const r = await planningAPI.recettes({ creer, valeurs, prix });
      onSaved(r.data.message);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" TransitionProps={{ onEnter: () => { setFichier(null); setRec(null); setError(''); } }}>
      <DialogTitle>Reprendre les recettes et les prix d'un fichier Excel</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
          Le fichier doit contenir un onglet de recettes (une ligne par type de poteau, des colonnes « Fer 10 (kg / poteau) », « Béton 8/16 (m3 / poteau) »…).
          Un onglet de budget (DÉSIGNATION, UNITÉ, PU) donne les prix. Vérifiez ci-dessous l'article du stock de chaque colonne : les quantités sont converties dans son unité
          (ciment en sacs de {SAC_KG} kg, m³ en tonnes selon la densité). Les types présents dans le fichier remplacent leur consommation actuelle.
        </Typography>
        <Box onClick={() => input.current?.click()} sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 2.5, textAlign: 'center', cursor: 'pointer' }}>
          <UploadFileOutlined sx={{ fontSize: 30, color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 600 }}>{fichier ? fichier.name : 'Choisir le fichier Excel (.xlsx)'}</Typography>
          <input ref={input} type="file" hidden accept=".xlsx" aria-label="Fichier Excel des recettes" onChange={(e) => { charger(e.target.files[0]); e.target.value = ''; }} />
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {calc && (
          <>
            <Typography sx={{ fontWeight: 700, mt: 2, mb: 1 }}>Onglet « {rec.feuille} » : {calc.lignes.length} type(s) de poteau, {rec.colonnes.length} intrant(s)</Typography>
            {calc.inconnus.length > 0 && <Alert severity="warning" sx={{ mb: 1 }}>Types absents de l'ERP, non repris : {calc.inconnus.join(', ')}</Alert>}
            {calc.erreurs.length > 0 && <Alert severity="error" sx={{ mb: 1 }}>{calc.erreurs.join(' · ')}</Alert>}
            {Object.keys(options).length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                {Object.keys(options).map((k) => (
                  <TextField key={k} select size="small" label={`Recette retenue — ${rec.lignes.find((l) => l.cle === k)?.libelle.replace(/option\s*\d+/i, '').trim()}`} value={options[k]}
                    onChange={(e) => setOptions((o) => ({ ...o, [k]: e.target.value }))} sx={{ minWidth: 260 }}>
                    {rec.lignes.filter((l) => l.cle === k).map((l) => <MenuItem key={l.option} value={String(l.option)}>{l.libelle}</MenuItem>)}
                  </TextField>
                ))}
                {rec.optionParam && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Option {rec.optionParam} choisie dans vos paramètres.</Typography>}
              </Box>
            )}
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Colonne du fichier</TableCell><TableCell>Article du stock</TableCell><TableCell>Conversion</TableCell><TableCell align="right">Prix du fichier</TableCell><TableCell align="right">Prix ERP</TableCell></TableRow></TableHead>
                <TableBody>
                  {calc.cols.map((c) => (
                    <TableRow key={c.j}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}><b>{c.nom}</b> <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>({c.src} / poteau)</Typography></TableCell>
                      <TableCell>
                        <TextField select size="small" value={c.m.cible} onChange={(e) => setM(c.j, 'cible', e.target.value)} sx={{ minWidth: 230 }} inputProps={{ 'aria-label': `Article pour ${c.nom}` }}>
                          {intrants.map((i) => <MenuItem key={i.intrant_id} value={i.intrant_id}>{i.designation} ({i.unite})</MenuItem>)}
                          <MenuItem value="creer"><em>Créer l'article « {nomArticle(c.nom)} »</em></MenuItem>
                          <MenuItem value="ignorer"><em>Ne pas reprendre</em></MenuItem>
                        </TextField>
                        {c.m.cible === 'creer' && (
                          <TextField select size="small" value={c.m.unite} onChange={(e) => setM(c.j, 'unite', e.target.value)} sx={{ ml: 1, width: 90 }} inputProps={{ 'aria-label': `Unité de ${c.nom}` }}>
                            {['kg', 't', 'sac', 'm3'].map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                          </TextField>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {c.m.cible === 'ignorer' ? '—' : c.k === null ? '?' : `1 ${c.src} = ${nf(c.k, 4)} ${c.unite}`}
                        {c.src === 'm3' && c.m.cible !== 'ignorer' && c.unite !== 'm3' && (
                          <TextField size="small" variant="standard" value={c.m.densite} onChange={(e) => setM(c.j, 'densite', e.target.value)} sx={{ ml: 1, width: 70 }}
                            inputProps={{ 'aria-label': `Densité ${c.nom}` }} InputProps={{ endAdornment: <Typography sx={{ fontSize: 11 }}>t/m³</Typography> }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{c.prixExcel ? `${nf(c.prixExcel.pu, 0)} F / ${c.prixExcel.unite}` : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, whiteSpace: 'nowrap', fontWeight: 600 }}>{c.prix !== null && c.m.cible !== 'ignorer' ? `${nf(c.prix, 2)} F / ${c.unite}` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Consommation par poteau reprise (dans l'unité du stock)</Typography>
            <TableContainer sx={{ maxHeight: 300, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader sx={{ '& td, & th': { px: 0.75 } }}>
                <TableHead><TableRow><TableCell>Type</TableCell>{calc.cols.filter((c) => c.m.cible !== 'ignorer').map((c) => <TableCell key={c.j} align="center" sx={{ fontSize: 12 }}>{c.it?.designation || c.nom}<br />({c.unite})</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {calc.lignes.map((l) => (
                    <TableRow key={l.libelle}>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{l.type}</TableCell>
                      {calc.cols.map((c, i) => (c.m.cible === 'ignorer' ? null : <TableCell key={c.j} align="center" sx={{ fontSize: 12.5 }}>{l.valeurs[i] && c.k !== null ? nf(l.valeurs[i] * c.k, 3) : '—'}</TableCell>))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {calc.cols.some((c) => c.m.cible === 'creer') && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Articles créés dans le stock intrants : {calc.cols.filter((c) => c.m.cible === 'creer').map((c) => `${nomArticle(c.nom)} (${c.unite})`).join(', ')}.
                Pensez à saisir leur stock (bouton Inventaire du stock intrants). Ils ne sont pas déstockés par le rapport journalier, qui ne connaît que le fer, le ciment, le béton 8/16 et le sable.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !calc?.lignes.length || calc.erreurs.length > 0} onClick={importer}>Reprendre les recettes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecettesDialog;
